import { Divider, Stack } from "@mui/material";
import type { JSX } from "react";
import { useCallback } from "react";
import type { FieldConfiguration } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { MetricField } from "../../../../../../../../../api/types/svTypes.ts";
import CustomLabel from "../../../../viewer/common/fields/CustomLabel.tsx";
import FieldOptionDeleteButton from "../../../../viewer/common/fields/FieldOptionDeleteButton.tsx";
import CustomId from "../../../api-report/editor/fields/CustomId.tsx";
import PeriodMenuItem from "./PeriodMenuItem.tsx";

interface Props {
  field: MetricField;
  otherOptions: JSX.Element;
  isApiReport?: boolean;
  cancel: () => void;
  saveChanges: (changes: Partial<FieldConfiguration>, causeClosing: boolean) => void;
  onRemove: () => void;
  readonly: boolean;
}

export default function MetricFieldOptions({
  field,
  otherOptions,
  cancel,
  saveChanges,
  onRemove,
  isApiReport = false,
  readonly,
}: Props) {
  const updateCustomLabel = useCallback(
    (customLabel: string | undefined) => {
      saveChanges({ customLabel }, false);
    },
    [saveChanges]
  );

  const updateCustomId = useCallback(
    (customId: string | undefined) => {
      saveChanges({ customId }, false);
    },
    [saveChanges]
  );

  const handleRemove = useCallback(() => {
    onRemove();
    cancel();
  }, [cancel, onRemove]);

  return (
    <>
      <CustomLabel
        customFieldValue={field.config.customLabel}
        originalFieldValue={field.meta.caption}
        updateCustomFieldValue={updateCustomLabel}
        readonly={readonly}
      />
      {isApiReport && (
        <CustomId
          customId={field.config.customId}
          defaultId={field.config.guid}
          updateCustomId={updateCustomId}
          readonly={readonly}
        />
      )}
      <OptionDivider />
      <Stack
        spacing={1.5}
        useFlexGap
        sx={{
          width: "100%",
          flex: 1,
          px: 2,
          py: 1.5,
        }}
      >
        <PeriodMenuItem metric={field} saveChanges={saveChanges} onClose={cancel} readonly={readonly} />
      </Stack>
      <OptionDivider />
      {otherOptions}
      <OptionDivider />
      {!readonly && <FieldOptionDeleteButton onRemove={handleRemove} />}
    </>
  );
}

const OptionDivider = () => <Divider style={{ marginBottom: 0, marginTop: 0 }} />;
